<!-- The Overview for an account in someone's groups: each group they're in and where they stand. -->
<script lang="ts">
  import { member } from "../../lib/member.svelte.ts";
  import { groupPath, link } from "../../lib/router.svelte.ts";
  import { colorOf } from "../../lib/colors.ts";
  import { formatMicro, shortDate } from "../../lib/format.ts";
  import { app } from "../../lib/store.svelte.ts";
  import { api } from "../../lib/api.ts";
  import SetupPrompt from "../../components/SetupPrompt.svelte";
  import BalanceAmount from "../../components/BalanceAmount.svelte";
  import * as A from "../../settings/amounts.ts";
  import { localNow } from "../../lib/tutoring.svelte.ts";
  import { isUnclaimed, myTutoringCard, overallBalance, shownCents, type Tutor, type TutoringEntry, type TutoringSlot, type TutoringStudent } from "@split/core";
  import { shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";

  // ---- one balance with the treasurer: each group, tutoring, and the overall
  interface MyTut {
    students: TutoringStudent[];
    tutors: Tutor[];
    entries: TutoringEntry[];
    slots: TutoringSlot[];
    open?: { tutorId: string; id: string; lessons: number }[];
  }
  let tut = $state<MyTut | null>(null);
  $effect(() => {
    api<MyTut>(`/api/me/tutoring?now=${localNow()}`)
      .then((r) => (tut = r))
      .catch(() => (tut = null));
  });
  const overall = $derived(
    overallBalance(
      (member.groups ?? []).filter((g) => g.me).map((g) => ({ name: g.name, balanceMicro: g.me!.balanceMicro })),
      tut?.students ?? [],
      tut?.tutors ?? [],
      tut?.entries ?? [],
    ),
  );
  const treasurer = $derived((member.groups ?? []).find((g) => g.treasurer)?.treasurer ?? "the treasurer");
  const hasTutoringLines = $derived(overall.lines.some((l) => l.kind === "tutoring"));
  /** Tutoring as a card next to the groups (a family's or tutor's own). */
  const tcard = $derived(
    tut && (tut.students.length || tut.tutors.length)
      ? myTutoringCard(
          tut.students,
          tut.tutors,
          tut.entries,
          tut.slots ?? [],
          {
            students: (tut.open ?? []).length,
            lessons: (tut.slots ?? []).filter((s) => isUnclaimed(s) && s.startsAt > localNow()).length,
          },
          localNow(),
        )
      : null,
  );
  const when = (dt: string) => `${shortDate(dt.slice(0, 10))} ${dt.slice(11, 16)}`;

  const groups = $derived(member.groups ?? []);
  const pending = $derived(member.requests.filter((r) => r.status === "pending"));
  const denied = $derived(member.requests.filter((r) => r.status === "denied"));
  $effect(() => {
    void member.refreshRequests();
  });
  // While waiting, check now and then so the group appears without a reload.
  $effect(() => {
    if (!pending.length) return;
    const t = setInterval(() => void member.refreshRequests(), 20_000);
    return () => clearInterval(t);
  });
  // When the treasurer approves, the group appears: refresh the list.
  $effect(() => {
    if (member.requests.some((r) => r.status === "approved" && !groups.some((g) => g.id === r.groupId))) {
      void member.refreshGroups();
    }
  });
  let asking = $state<string | null>(null);
  let joinError = $state("");
  async function join(groupId: string, personId: string | null) {
    asking = groupId;
    joinError = "";
    try {
      const status = await member.join(groupId, personId);
      if (status === "joined") app.toast("You're in. Everything in the group is under its name.");
    } catch (e) {
      joinError = (e as Error).message;
    } finally {
      asking = null;
    }
  }
</script>

<div class="page-head">
  <div>
    <h1>Overview</h1>
    <p class="lede">
      Hi, {app.userName}.
      {#if member.groups === null}
        Getting your groups from the server.
      {:else if groups.length === 0}
        {pending.length ? "Waiting for the treasurer to let you in." : "You're not in any groups yet. Pick the ones you're in below."}
      {/if}
      {#if member.stale}Showing the last saved copy; you're offline.{/if}
    </p>
  </div>
</div>
<SetupPrompt />

{#if overall.lines.length > 1 || hasTutoringLines}
  <section class="overall" aria-label="Your balance with {treasurer}">
    <h2 class="bal"><span>{A.MY_BALANCE_LABEL}</span><BalanceAmount cents={overall.netCents} big /></h2>
    <ul>
      {#each overall.lines as l, i (i)}
        <li>
          <span><span class="tag">{l.kind === "group" ? A.MY_SOURCE_GROUP_TAG : A.MY_SOURCE_TUTORING_TAG}</span> {l.label}</span>
          <BalanceAmount cents={l.cents} />
        </li>
      {/each}
    </ul>
  </section>
{/if}

{#if pending.length || denied.length}
  <section class="asks">
    {#each pending as r (r.id)}
      <div class="ask"><span class="pill wait">Waiting</span><span>Asked to join <strong>{r.groupName}</strong>. It shows up here as soon as the treasurer confirms.</span></div>
    {/each}
    {#each denied.filter((d) => !groups.some((g) => g.id === d.groupId) && !pending.some((p) => p.groupId === d.groupId)) as r (r.id)}
      <div class="ask"><span class="pill">Not approved</span><span>The treasurer didn't confirm your request for {r.groupName}. Ask them directly if that's a mistake.</span></div>
    {/each}
  </section>
{/if}

{#if member.joinable.length}
  <section class="asks" aria-label="Join a group">
    <h2 class="join-h">{groups.length ? "In another group?" : "Which groups are you in?"}</h2>
    {#each member.joinable as g (g.groupId)}
      {@const free = g.matches.filter((m) => !m.taken)}
      <div class="ask">
        <span><strong>{g.name}</strong> <span class="muted small">{g.joining === "open" ? "· join right away" : "· the treasurer confirms"}</span></span>
        <span class="joins">
          {#each free as m (m.personId)}
            <button class="btn small" disabled={asking === g.groupId} onclick={() => join(g.groupId, m.personId)}>I'm {m.name}</button>
          {/each}
          <button class="btn small" class:primary={!free.length} disabled={asking === g.groupId} onclick={() => join(g.groupId, null)}>{free.length ? "I'm someone new" : "I'm in this group"}</button>
        </span>
      </div>
    {/each}
    {#if joinError}<p class="owes" role="alert">{joinError}</p>{/if}
  </section>
{/if}

<div class="grid">
  {#each groups as g (g.id)}
    <a class="group" href={groupPath(g.id)} use:link style:--c={colorOf(g.me?.color)}>
      <h2>{g.name}</h2>
      {#if g.me}
        <p class="big"><BalanceAmount cents={shownCents(g.me.balanceMicro)} big /></p>
        <dl>
          <div><dt>You're</dt><dd><span class="dot" aria-hidden="true"></span>{g.me.name}</dd></div>
          <div><dt>Your items</dt><dd>{g.itemCount}</dd></div>
          <div><dt>Your total</dt><dd class="num">{formatMicro(g.me.totalMicro)}</dd></div>
          <div><dt>Last item</dt><dd>{g.lastDate ? shortDate(g.lastDate) : "None yet"}</dd></div>
        </dl>
      {:else}
        <p class="muted">The treasurer removed you from this group.</p>
      {/if}
    </a>
  {/each}
  {#if tcard}
    <a class="group" href="/tutoring" use:link aria-label={TT.OV_TUTORING_TITLE}>
      <h2>{TT.OV_TUTORING_TITLE}</h2>
      <p class="big"><BalanceAmount cents={tcard.balanceCents} big /></p>
      <dl>
        <div><dt>{TT.OV_TUTORING_LESSONS}</dt><dd>{tcard.lessonsThisMonth}</dd></div>
        <div><dt>{TT.OV_TUTORING_NEXT}</dt><dd>{tcard.nextLesson ? when(tcard.nextLesson) : TT.OV_TUTORING_NONE}</dd></div>
        {#if tut?.tutors.length && shows(TT.OV_TUTORING_UP)}<div><dt>{TT.OV_TUTORING_UP}</dt><dd>{tcard.upForGrabs}</dd></div>{/if}
      </dl>
    </a>
  {/if}
</div>

<style>
  .overall {
    display: grid;
    gap: 8px;
    padding: 16px 18px;
    margin-bottom: 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
  }

  .overall h2 {
    margin: 0;
  }

  .overall ul {
    display: grid;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .overall li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    border-top: 1px solid var(--rule);
    font-size: var(--t-sm);
  }

  .overall .tag {
    margin-right: 4px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--wash);
    color: var(--ink-2);
    font-size: 0.6875rem;
    font-weight: 700;
  }

  .bal {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: baseline;
  }

  .join-h {
    margin: 4px 0 0;
    font-size: var(--t-lg);
  }

  .ask > .joins {
    flex: 0 1 auto;
  }

  .joins {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: auto;
  }

  .asks {
    display: grid;
    gap: 8px;
    margin-bottom: 18px;
  }

  .ask {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 14px;
    border-radius: var(--r-md);
    background: var(--surface);
    border: 1px solid var(--rule);
  }

  .ask > span:not(.pill):not(.dot) {
    flex: 1 1 200px;
  }

  .pill {
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--wash);
    font-size: var(--t-xs);
    font-weight: 650;
  }

  .pill.wait {
    color: #8b5cd6;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
  }

  .group {
    display: grid;
    gap: 4px;
    align-content: start;
    padding: 18px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule), inset 4px 0 0 var(--c);
    color: var(--ink);
    text-decoration: none;
  }

  .group:hover {
    box-shadow: inset 0 0 0 1px var(--ink-2), inset 4px 0 0 var(--c);
  }

  h2 {
    margin: 0;
    font-size: var(--t-lg);
  }

  .big {
    margin: 6px 0 0;
    font-size: var(--t-2xl);
    font-weight: 700;
    line-height: 1.1;
  }

  .muted {
    margin: 0;
    font-size: var(--t-sm);
  }

  dl {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px 12px;
    margin: 10px 0 0;
    padding-top: 12px;
    border-top: 1px solid var(--rule);
  }

  dt {
    font-size: var(--t-xs);
    color: var(--muted);
  }

  dd {
    margin: 0;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--c);
  }
</style>
